
// src\components\main\widgets\WidgetsRender.tsx
import { ComponentType } from "react";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
import { getSettings } from "@/core/lib/api/main/setting";
import AnimatedSection from "./AnimatedSection";
import { filterPublishedWidgets } from "@/core/lib/widget-status";

const Banner = loadThemeComponent("Banner")
const SwiperList = loadThemeComponent("SwiperList")
const ProductBar = loadThemeComponent("ProductBar")
const PostListText = loadThemeComponent("PostListText")
const TextWidget = loadThemeComponent("TextWidget")
const ImageWidget = loadThemeComponent("ImageWidget")
const VideoBanner = loadThemeComponent("VideoBanner");
const GalleryWidget = loadThemeComponent("GalleryWidget")
const GridItems = loadThemeComponent("GridItems")
const Thirdgrid = loadThemeComponent("Thirdgrid")
const ThirdGridLine = loadThemeComponent("ThirdGridLine")
const EditorWidget = loadThemeComponent("EditorWidget")
const FullSliderWidget = loadThemeComponent("FullSliderWidget")
// ویجت‌های جدید (فعلاً فقط تم lino این‌ها رو داره؛ در تم‌های دیگه اگر
// فایل متناظر نباشه، loadThemeComponent با fallback به default برمی‌گرده)
const StickyStory = loadThemeComponent("StickyStory")
const ScrollGallery = loadThemeComponent("ScrollGallery")
const SpecGridWidget = loadThemeComponent("SpecGridWidget")
const NewsGrid = loadThemeComponent("NewsGrid")
const ScrollShowcase = loadThemeComponent("ScrollShowcase")
const ProductScene = loadThemeComponent("ProductScene")


const componentList: Record<string, ComponentType<any>> = {
    banner_1: Banner,
    swiper_list_1: SwiperList,
    product_list: ProductBar,
    post_list_text_1: PostListText,
    text: TextWidget,
    image: ImageWidget,
    video_banner: VideoBanner,
    slider: SwiperList,
    grid_items: GridItems,
    third_grid: Thirdgrid,
    third_grid_line: ThirdGridLine,
    gallery: GalleryWidget,
    editor: EditorWidget,
    full_slider: FullSliderWidget,
    sticky_story: StickyStory,
    showcase_slider: ScrollGallery,
    spec_grid: SpecGridWidget,
    news_grid: NewsGrid,
    scroll_showcase: ScrollShowcase,
    product_scene: ProductScene,
};

// ویجت‌هایی که عرض متغیر (width: 1/1, 1/2, 1/3, 2/3) دارن
const VARIABLE_WIDTH_TYPES = new Set(['text', 'image', 'editor']);

// ویجت‌هایی که خودشون از position:sticky برای افکت اسکرول‌جکینگ استفاده می‌کنن؛
// نباید توی AnimatedSection (که transform روی خودش داره) پیچیده بشن، چون
// transform روی یک والد، position:sticky فرزندهاش رو نسبت به viewport می‌شکنه
const SCROLL_JACKING_TYPES = new Set(['scroll_showcase']);

const widthMap: Record<string, string> = {
    '1/1': 'col-span-12',
    '1/2': 'col-span-12 md:col-span-6',
    '1/3': 'col-span-12 md:col-span-4',
    '1/4': 'col-span-12 md:col-span-3',
    '2/3': 'col-span-12 md:col-span-8',
    '3/4': 'col-span-12 md:col-span-9',
};

// پدینگ کناری فقط برای ویجت‌های عرض‌متغیر (تصویر/متن/ادیتور)
// این مقدار را با کلاس پدینگ کامپوننت Container خودتان هماهنگ کنید
const CONTAINER_PADDING = 'px-4 sm:px-6 lg:px-12';

export interface WidgetItem {
    id: string | number;
    template_coding?: string;
    type?: string;
    width?: string;
    [key: string]: any;
}

interface WidgetsRenderProps {
    widgets?: WidgetItem[];
    // وقتی true باشه، به ویجت‌هایی که ازش پشتیبانی می‌کنن (فعلاً text و editor
    // در تم لینو) گفته می‌شه که انیمیشن ورود متن (مثل صفحه‌ی محصولات اپل) رو
    // فعال کنن. فقط از صفحه‌ی اصلی تم لینو پاس داده می‌شه، جای دیگه‌ای تغییر نمی‌کنه.
    animateText?: boolean;
    // فقط از MainView صفحه‌ی اصلی true پاس داده می‌شه. چون صفحه‌ی اصلی
    // برخلاف بقیه‌ی صفحات داخل Container (w-9/10) رندر نمی‌شه، بعضی
    // ویجت‌ها (مثل GridItems/Thirdgrid/ProductBar/SwiperList) که فقط یک
    // p-4 ثابت برای فاصله‌ی کناری دارن، اونجا بی‌فاصله و چسبیده به لبه
    // دیده می‌شن. همون ویجت‌ها خودشون این پرچم رو می‌گیرن و فقط پدینگ
    // کناری خودشون رو (نه بقیه‌ی ویجت‌ها رو) بیشتر می‌کنن.
    isHome?: boolean;
}

const THEMES_WITH_SCROLL_ANIMATION = ['tolica', 'lino'];
const isTolicaTheme = THEMES_WITH_SCROLL_ANIMATION.includes(
    process.env.NEXT_PUBLIC_THEME || ''
);

export default async function WidgetsRender({ widgets, animateText = false, isHome = false }: WidgetsRenderProps) {
    if (!widgets) return null;
    if (!Array.isArray(widgets)) {
        return null;
    }

    // ویجت‌های پیش‌نویس (status = draft) در هیچ‌جا نمایش داده نمی‌شن
    const publishedWidgets = filterPublishedWidgets(widgets);

    // سمت سرور فچ می‌شه (نه با هوکی مثل useSiteSettings سمت کلاینت) تا
    // ویجت‌هایی مثل ProductBar که قیمت محصول رو نشون می‌دن (قیمتی که وابسته
    // به تنظیماتیه مثل درصد مالیات)، از همون اولین رندر قیمت درست رو داشته
    // باشن؛ نه اینکه اول بدون تنظیمات (و درنتیجه بدون مالیات) رندر بشن و بعد
    // از لود شدنِ async تنظیمات سمت کلاینت، قیمت یهو عوض بشه یا اصلاً عوض نشه.
    const settings = await getSettings();

    return (
        <>
            {
                publishedWidgets.map((widget, index) => {
                    const typeKey = widget.template_coding || widget.type || "";
                    const Component = componentList[typeKey];

                    if (!Component) {
                        console.warn('No component found for widget:', widget.id);
                        return null;
                    }

                    const key = `widget-${widget.id ?? index}-${index}`;
                    const isVariable = VARIABLE_WIDTH_TYPES.has(typeKey);

                    const spanClass = isVariable
                        ? (widthMap[widget.width || '1/1'] || 'col-span-12')
                        : 'col-span-12';

                    const paddingClass = '';

                    if (isTolicaTheme && !SCROLL_JACKING_TYPES.has(typeKey)) {
                        return (
                            <AnimatedSection
                                key={key}
                                className={`${spanClass} ${paddingClass}`}
                            >
                                <Component data={widget} animateText={animateText} isHome={isHome} settings={settings} />
                            </AnimatedSection>
                        );
                    }

                    return <Component key={key} data={widget} animateText={animateText} isHome={isHome} settings={settings} />;
                })
            }
        </>
    );
}